import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../supabaseClient';
import { defaultIndexForCcy } from '../utils/importFinancingUtils';

// ── Date parser — handles DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD-Mon-YYYY, Excel serial
function parseDate(val) {
  if (!val) return null;
  if (typeof val === 'number') {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`;
  }
  const str = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const dmy = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`;
  const mdy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) return `${mdy[3]}-${mdy[1].padStart(2,'0')}-${mdy[2].padStart(2,'0')}`;
  const dmonY = str.match(/^(\d{1,2})[- ]([A-Za-z]{3})[- ](\d{4})$/);
  if (dmonY) {
    const months = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',
                    jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
    const m = months[dmonY[2].toLowerCase()];
    if (m) return `${dmonY[3]}-${m}-${dmonY[1].padStart(2,'0')}`;
  }
  const native = new Date(str);
  if (!isNaN(native)) return native.toISOString().split('T')[0];
  return null;
}

function parseNum(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = parseFloat(String(val).replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? null : n;
}

// ── Template definitions ───────────────────────────────────────
const LC_HEADERS = [
  { key: 'lc_ref',              label: 'LC Ref',                  required: true,  example: 'LC-2024-001', note: 'Unique reference' },
  { key: 'lc_type',             label: 'Type',                    required: false, example: 'Sight',       note: 'Sight or Usance' },
  { key: 'beneficiary',         label: 'Beneficiary',             required: true,  example: 'Sinopec Ltd', note: 'Supplier name' },
  { key: 'issuing_bank',        label: 'Issuing Bank',            required: false, example: 'HDFC Bank',   note: '' },
  { key: 'currency',            label: 'Currency',                required: true,  example: 'USD',         note: 'USD / EUR / GBP / SGD' },
  { key: 'amount_fcy',          label: 'Amount (FCY)',            required: true,  example: '500000',      note: 'Numbers only, no commas' },
  { key: 'issue_date',          label: 'Issue Date',              required: true,  example: '01/04/2026',  note: 'DD/MM/YYYY preferred' },
  { key: 'usance_period_days', label: 'Usance Period (Days)', required: false, example: '90',                    note: 'Only for Usance LC — 30/60/90/120/150/180' },
{ key: 'usance_from',        label: 'Usance From',          required: false, example: 'Bill of Lading',        note: 'Bill of Lading / Sight of Documents / Port of Discharge' },
  { key: 'maturity_date',       label: 'Maturity Date',           required: true,  example: '30/06/2026',  note: 'DD/MM/YYYY preferred' },
  { key: 'commission_rate',     label: 'Commission Rate (%)',      required: true,  example: '0.75',        note: '% p.a. e.g. 0.75' },
  { key: 'issuance_fee_inr',    label: 'Issuance Fee (INR)',       required: true,  example: '15000',       note: 'INR amount' },
  { key: 'swift_fee_inr',       label: 'SWIFT Fee (INR)',          required: false, example: '5000',        note: 'INR amount' },
  { key: 'negotiation_fee_inr', label: 'Negotiation Fee (INR)',    required: false, example: '8000',        note: 'INR amount' },
  { key: 'amendment_fee_inr',   label: 'Amendment Fee (INR)',      required: false, example: '0',           note: 'INR amount' },
  { key: 'other_fee_inr',       label: 'Other Charges (INR)',      required: false, example: '0',           note: 'INR amount' },
  { key: 'hedge_status',        label: 'Hedge Status',            required: false, example: 'Unhedged',    note: 'Unhedged / Partially Hedged / Fully Hedged' },
  { key: 'hedged_amount_fcy',   label: 'Hedged Amount (FCY)',      required: false, example: '0',           note: '0 if unhedged' },
  { key: 'contracted_rate_inr', label: 'Contracted Rate (INR)',    required: false, example: '',            note: 'Forward rate if hedged' },
  { key: 'spot_rate_inr',   label: 'Spot Rate (INR)',   required: false, example: '83.50', note: 'Live spot at time of issue' },
{ key: 'bank_reference',  label: 'Bank Reference',    required: false, example: 'HDFC/LC/2024/001', note: 'Bank internal reference' },
];

const BC_HEADERS = [
  { key: 'bc_ref',              label: 'BC Ref',                  required: true,  example: 'BC-2024-001', note: 'Unique reference' },
  { key: 'lc_ref',              label: 'Linked LC Ref',           required: false, example: 'LC-2024-001', note: 'Leave blank if standalone' },
  { key: 'lender_bank',         label: 'Lender Bank',             required: true,  example: 'DBS Bank',    note: '' },
  { key: 'currency',            label: 'Currency',                required: true,  example: 'USD',         note: 'USD / EUR / GBP / SGD' },
  { key: 'amount_fcy',          label: 'Amount (FCY)',            required: true,  example: '500000',      note: 'Numbers only, no commas' },
  { key: 'drawdown_date',       label: 'Drawdown Date',           required: true,  example: '01/04/2026',  note: 'DD/MM/YYYY preferred' },
  { key: 'repayment_date',      label: 'Repayment Date',          required: true,  example: '30/06/2026',  note: 'DD/MM/YYYY preferred' },
  { key: 'tenor_days',          label: 'Tenor (Days)',            required: false, example: '90',          note: 'Auto-calculated if dates provided' },
  { key: 'index_type',          label: 'Index',                   required: true,  example: 'SOFR',        note: 'USD→SOFR  EUR→€STR  GBP→SONIA  SGD→SORA  or Fixed' },
  { key: 'index_rate',          label: 'Benchmark Rate (%)',       required: true,  example: '5.33',        note: '% p.a. e.g. 5.33' },
  { key: 'spread_rate',         label: 'Spread (%)',              required: true,  example: '0.15',        note: '% p.a. e.g. 0.15' },
  { key: 'all_in_rate',         label: 'All-in Rate (%)',         required: false, example: '5.48',        note: 'Auto-calc = Benchmark + Spread' },
  { key: 'hedge_status',        label: 'Hedge Status',            required: false, example: 'Unhedged',    note: 'Unhedged / Partially Hedged / Fully Hedged' },
  { key: 'hedged_amount_fcy',   label: 'Hedged Amount (FCY)',      required: false, example: '0',           note: '0 if unhedged' },
  { key: 'contracted_rate_inr', label: 'Contracted Rate (INR)',    required: false, example: '',            note: 'Forward rate if hedged' },
  { key: 'spot_rate_inr', label: 'Spot Rate (INR)', required: false, example: '83.50', note: 'Live spot at time of drawdown' },
];

// ── Flexible header matching ───────────────────────────────────
const LC_ALIASES = {
  lc_ref:              ['lc ref','lc_ref','lc reference','reference','ref'],
  lc_type:             ['type','lc type','lc_type'],
  beneficiary:         ['beneficiary','supplier','counterparty'],
  issuing_bank:        ['issuing bank','issuing_bank','bank'],
  currency:            ['currency','ccy'],
  amount_fcy:          ['amount','amount fcy','fcy amount','lc amount','amount (fcy)'],
  issue_date:          ['issue date','issue_date','opened date','date of issue','issue date'],
  maturity_date:       ['maturity date','maturity_date','expiry date','due date','maturity date'],
  commission_rate:     ['commission','commission rate','commission_rate','commission rate (%)'],
  issuance_fee_inr:    ['issuance fee','issuance_fee','opening fee','issuance fee (inr)'],
  swift_fee_inr:       ['swift fee','swift_fee','telex fee','swift fee (inr)'],
  negotiation_fee_inr: ['negotiation fee','negotiation_fee','negotiation fee (inr)'],
  amendment_fee_inr:   ['amendment fee','amendment_fee','amendment fee (inr)'],
  other_fee_inr:       ['other fee','other charges','other_fee','other charges (inr)'],
  hedge_status:        ['hedge status','hedge_status','hedged'],
  hedged_amount_fcy:   ['hedged amount','hedged_amount','hedge amount','hedged amount (fcy)'],
  contracted_rate_inr: ['contracted rate','contracted_rate','forward rate','contracted rate (inr)'],
  usance_period_days: ['usance period','usance period (days)','usance days','usance_period_days','usance period days'],
usance_from:        ['usance from','usance_from','usance period from','from'],
spot_rate_inr:  ['spot rate','spot_rate','spot rate inr','spot'],
bank_reference: ['bank reference','bank_reference','bank ref'],
};

const BC_ALIASES = {
  bc_ref:              ['bc ref','bc_ref','bc reference','reference','ref'],
  lc_ref:              ['lc ref','lc_ref','linked lc','lc reference','linked lc ref'],
  lender_bank:         ['lender','lender bank','lender_bank','bank'],
  currency:            ['currency','ccy'],
  amount_fcy:          ['amount','amount fcy','bc amount','fcy amount','amount (fcy)'],
  drawdown_date:       ['drawdown date','drawdown_date','draw down date','disbursement date','drawdown date'],
  repayment_date:      ['repayment date','repayment_date','maturity date','due date','repayment date'],
  tenor_days:          ['tenor','tenor days','tenor_days','days','tenor (days)'],
  index_type:          ['index','index type','index_type','benchmark'],
  index_rate:          ['index rate','index_rate','benchmark rate','benchmark rate (%)','sofr','estr','sonia'],
  spread_rate:         ['spread','spread rate','spread_rate','margin','spread (%)'],
  all_in_rate:         ['all in','all-in','all_in_rate','all in rate','total rate','all-in rate (%)'],
  hedge_status:        ['hedge status','hedge_status','hedged'],
  hedged_amount_fcy:   ['hedged amount','hedged_amount','hedge amount','hedged amount (fcy)'],
  contracted_rate_inr: ['contracted rate','contracted_rate','forward rate','contracted rate (inr)'],
  spot_rate_inr: ['spot rate','spot_rate','spot rate inr','usdinr','spot'],
};

function mapRow(rawObj, aliasMap) {
  const result = {};
  const lowerKeys = {};
  Object.keys(rawObj).forEach(k => { lowerKeys[k.toLowerCase().trim()] = rawObj[k]; });
  Object.entries(aliasMap).forEach(([field, aliases]) => {
    for (const alias of aliases) {
      if (lowerKeys[alias] !== undefined) { result[field] = lowerKeys[alias]; break; }
    }
  });
  return result;
}

// ── Template download ──────────────────────────────────────────
function downloadTemplate(isLC) {
  const headers = isLC ? LC_HEADERS : BC_HEADERS;
  const wb      = XLSX.utils.book_new();

  // Sheet 1: Data entry sheet
  const headerRow  = headers.map(h => h.label);
  const exampleRow = headers.map(h => h.example);
  const ws = XLSX.utils.aoa_to_sheet([headerRow, exampleRow, exampleRow]);
  ws['!cols'] = headers.map(() => ({ wch: 24 }));
  XLSX.utils.book_append_sheet(wb, ws, isLC ? 'LC Upload' : 'BC Upload');

  // Sheet 2: Reference guide
  const refRows = [
    ['NEBULA FINANCE — ' + (isLC ? 'LC' : 'Buyers Credit') + ' Upload Template'],
    [],
    ['Column', 'Required?', 'Format / Notes'],
    ...headers.map(h => [
      h.label,
      h.required ? 'REQUIRED ✱' : 'Optional',
      h.note || '',
    ]),
    [],
    ['── USANCE (LC only) ──', '', ''],
['Usance Period (Days)', '', '30 / 60 / 90 / 120 / 150 / 180 — only fill for Usance LC'],
['Usance From',          '', 'Bill of Lading  |  Sight of Documents  |  Port of Discharge'],
[],
    ['── DATE FORMAT ──', '', ''],
    ['Preferred', '', 'DD/MM/YYYY  e.g. 30/06/2026'],
    ['Also accepted', '', 'MM/DD/YYYY  |  YYYY-MM-DD  |  DD-Jun-2026  |  Excel date cells'],
    [],
    ['── NUMBERS ──', '', ''],
    ['Format', '', 'No commas or currency symbols. e.g. 500000 not 5,00,000'],
    [],
    ['── CURRENCY ──', '', ''],
    ['Accepted values', '', 'USD  EUR  GBP  SGD  JPY  AED'],
    ...(!isLC ? [
      [],
      ['── BENCHMARK INDEX ──', '', ''],
      ['USD', '', 'SOFR or Fixed'],
      ['EUR', '', '€STR or Fixed'],
      ['GBP', '', 'SONIA or Fixed'],
      ['SGD', '', 'SORA or Fixed'],
      ['All-in Rate', '', 'Leave blank — auto-calculated as Benchmark Rate + Spread'],
    ] : []),
    [],
    ['── HEDGE STATUS ──', '', ''],
    ['Values', '', 'Unhedged  |  Partially Hedged  |  Fully Hedged'],
  ];
  const refWs = XLSX.utils.aoa_to_sheet(refRows);
  refWs['!cols'] = [{ wch: 28 }, { wch: 14 }, { wch: 52 }];
  XLSX.utils.book_append_sheet(wb, refWs, 'Reference Guide');

  XLSX.writeFile(wb, isLC ? 'Nebula_LC_Upload_Template.xlsx' : 'Nebula_BC_Upload_Template.xlsx');
}

// ── Validation ─────────────────────────────────────────────────
function validateLC(row, i) {
  const errs = [];
  const n = i + 2; // row number (1 = header, 2 = first data)
  if (!row.lc_ref)          errs.push(`Row ${n}: LC Ref is required`);
  if (!row.beneficiary)     errs.push(`Row ${n}: Beneficiary is required`);
  if (!row.currency)        errs.push(`Row ${n}: Currency is required`);
  if (!row.amount_fcy)      errs.push(`Row ${n}: Amount is required`);
  if (!row.issue_date)      errs.push(`Row ${n}: Issue Date is required`);
  if (!row.maturity_date)   errs.push(`Row ${n}: Maturity Date is required`);
  if (!row.commission_rate) errs.push(`Row ${n}: Commission Rate is required`);
  if (!row.issuance_fee_inr && row.issuance_fee_inr !== 0) errs.push(`Row ${n}: Issuance Fee is required`);
  return errs;
}

function validateBC(row, i) {
  const errs = [];
  const n = i + 2;
  if (!row.bc_ref)        errs.push(`Row ${n}: BC Ref is required`);
  if (!row.lender_bank)   errs.push(`Row ${n}: Lender Bank is required`);
  if (!row.currency)      errs.push(`Row ${n}: Currency is required`);
  if (!row.amount_fcy)    errs.push(`Row ${n}: Amount is required`);
  if (!row.drawdown_date) errs.push(`Row ${n}: Drawdown Date is required`);
  if (!row.repayment_date)errs.push(`Row ${n}: Repayment Date is required`);
  if (!row.index_type)    errs.push(`Row ${n}: Index is required`);
  if (row.index_rate === undefined || row.index_rate === '') errs.push(`Row ${n}: Benchmark Rate is required`);
  if (row.spread_rate === undefined || row.spread_rate === '') errs.push(`Row ${n}: Spread is required`);
  return errs;
}

// ── Main component ─────────────────────────────────────────────
export default function ExcelUploadModal({ type, onClose, onSaved, companyId }) {
  const [rows,     setRows]     = useState([]);
  const [errors,   setErrors]   = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [fileName, setFileName] = useState('');
  const fileRef = useRef();

  const isLC      = type === 'lc';
  const aliasMap  = isLC ? LC_ALIASES : BC_ALIASES;
  const headers   = isLC ? LC_HEADERS : BC_HEADERS;
  const reqFields = headers.filter(h => h.required).map(h => h.label);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setErrors([]); setWarnings([]); setSaved(false); setRows([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb  = XLSX.read(evt.target.result, { type: 'binary', cellDates: false });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (raw.length < 2) { setErrors(['File appears empty — need at least a header row and one data row.']); return; }

      const sheetHeaders = raw[0].map(h => String(h).trim());
      const dataRows     = raw.slice(1).filter(r => r.some(c => c !== ''));

      const parsed = dataRows.map(r => {
        const obj = {};
        sheetHeaders.forEach((h, j) => { obj[h] = r[j]; });
        return mapRow(obj, aliasMap);
      });

      // Warn about dates that couldn't be parsed
      const dateWarnings = [];
      parsed.forEach((row, i) => {
        const dateFields = isLC
          ? ['issue_date', 'maturity_date']
          : ['drawdown_date', 'repayment_date'];
        dateFields.forEach(f => {
          if (row[f] && !parseDate(row[f])) {
            dateWarnings.push(`Row ${i+2}: "${row[f]}" in ${f} — unrecognised date format, will be skipped`);
          }
        });
      });
      if (dateWarnings.length) setWarnings(dateWarnings);
      setRows(parsed);
    };
    reader.readAsBinaryString(file);
  }

async function handleUpload() {
    const allErrors = rows.flatMap((r, i) => isLC ? validateLC(r, i) : validateBC(r, i));
    if (allErrors.length) { setErrors(allErrors); return; }

    setSaving(true);

    if (isLC) {
      const records = rows.map(r => ({
        company_id:          companyId,
        lc_ref:              String(r.lc_ref || '').trim(),
        lc_type:             r.lc_type || 'Sight',
        beneficiary:         String(r.beneficiary || '').trim(),
        issuing_bank:        String(r.issuing_bank || '').trim(),
        currency:            String(r.currency || 'USD').trim().toUpperCase(),
        amount_fcy:          parseNum(r.amount_fcy),
        issue_date:          parseDate(r.issue_date),
        maturity_date:       parseDate(r.maturity_date),
        commission_rate:     parseNum(r.commission_rate),
        issuance_fee_inr:    parseNum(r.issuance_fee_inr) ?? 0,
        swift_fee_inr:       parseNum(r.swift_fee_inr) ?? 0,
        negotiation_fee_inr: parseNum(r.negotiation_fee_inr) ?? 0,
        amendment_fee_inr:   parseNum(r.amendment_fee_inr) ?? 0,
        other_fee_inr:       parseNum(r.other_fee_inr) ?? 0,
        hedge_status:        r.hedge_status || 'Unhedged',
        hedged_amount_fcy:   parseNum(r.hedged_amount_fcy) ?? 0,
        contracted_rate_inr: parseNum(r.contracted_rate_inr),
        usance_period_days:  r.usance_period_days ? parseInt(r.usance_period_days) : null,
        usance_from:         r.usance_from || null,
        spot_rate_inr:       parseNum(r.spot_rate_inr),
        bank_reference:      r.bank_reference ? String(r.bank_reference).trim() : null,
        status:              'Active',
      }));
      const { error } = await supabase.from('import_financing').insert(records);
      if (error) { setErrors([error.message]); setSaving(false); return; }

    } else {
      const records = rows.map(r => {
        const ccy       = String(r.currency || 'USD').trim().toUpperCase();
        const indexType = r.index_type ? String(r.index_type).trim() : defaultIndexForCcy(ccy);
        const idxRate   = parseNum(r.index_rate);
        const spdRate   = parseNum(r.spread_rate);
        const allIn     = parseNum(r.all_in_rate) ??
                          (indexType === 'Fixed'
                            ? idxRate
                            : (idxRate !== null && spdRate !== null ? +(idxRate + spdRate).toFixed(4) : null));
        let tenor = parseNum(r.tenor_days);
        if (!tenor && r.drawdown_date && r.repayment_date) {
          const dd = parseDate(r.drawdown_date);
          const rd = parseDate(r.repayment_date);
          if (dd && rd) tenor = Math.round((new Date(rd) - new Date(dd)) / 86400000);
        }
        return {
          company_id:          companyId,
          bc_ref:              String(r.bc_ref || '').trim(),
          lc_ref:              r.lc_ref ? String(r.lc_ref).trim() : null,
          lender_bank:         String(r.lender_bank || '').trim(),
          currency:            ccy,
          amount_fcy:          parseNum(r.amount_fcy),
          drawdown_date:       parseDate(r.drawdown_date),
          repayment_date:      parseDate(r.repayment_date),
          tenor_days:          tenor ?? 90,
          index_type:          indexType,
          index_rate:          idxRate,
          spread_rate:         spdRate,
          all_in_rate:         allIn,
          spot_rate_inr:       parseNum(r.spot_rate_inr),
          hedge_status:        r.hedge_status || 'Unhedged',
          hedged_amount_fcy:   parseNum(r.hedged_amount_fcy) ?? 0,
          contracted_rate_inr: parseNum(r.contracted_rate_inr),
          status:              'Active',
        };
      });

      // Resolve lc_id from lc_ref
      for (const record of records) {
        if (record.lc_ref) {
          const { data: lcRow } = await supabase
            .from('import_financing')
            .select('id')
            .eq('lc_ref', record.lc_ref)
            .eq('company_id', companyId)
            .maybeSingle();
          if (lcRow) record.lc_id = lcRow.id;
        }
      }

      const { error } = await supabase.from('import_financing_bc').insert(records);
      if (error) { setErrors([error.message]); setSaving(false); return; }
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => onSaved(), 1200);
  }

  const previewCols = rows.length > 0
    ? Object.keys(rows[0]).filter(k => rows[0][k] !== undefined && rows[0][k] !== '').slice(0, 5)
    : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-[620px] max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-white">
              Upload {isLC ? 'LC' : 'BC'} Records — Excel
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Use our template or upload your own file — flexible column matching
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
        </div>

        {/* Two options */}
        <div className="grid grid-cols-2 gap-3 mb-5">

          {/* Option A — Template */}
          <div className="border border-teal-800 bg-teal-900/20 rounded-xl p-4">
            <div className="text-xs font-medium text-teal-400 mb-1 uppercase tracking-wider">Option A — Template</div>
            <p className="text-xs text-gray-400 mb-3">
              Download our pre-formatted template with all required columns and example data.
            </p>
            <div className="text-xs text-gray-500 mb-3 space-y-0.5">
              <div className="text-amber-400 font-medium mb-1">Required fields:</div>
              {headers.filter(h => h.required).map(h => (
                <div key={h.key} className="flex items-center gap-1.5">
                  <span className="text-amber-400">✱</span>
                  <span>{h.label}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => downloadTemplate(isLC)}
              className="w-full py-2 text-xs bg-teal-700 hover:bg-teal-600 text-white rounded-lg transition-colors font-medium"
            >
              ↓ Download Template
            </button>
          </div>

          {/* Option B — Own file */}
          <div className="border border-gray-700 bg-gray-800/40 rounded-xl p-4">
            <div className="text-xs font-medium text-gray-300 mb-1 uppercase tracking-wider">Option B — Your File</div>
            <p className="text-xs text-gray-400 mb-3">
              Upload your own Excel. Headers are matched flexibly — exact names not required.
            </p>
            <div className="text-xs text-gray-500 mb-3 space-y-0.5">
              <div className="text-gray-400 font-medium mb-1">Date formats accepted:</div>
              {['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-Jun-2026', 'Excel date cells'].map(f => (
                <div key={f} className="flex items-center gap-1.5">
                  <span className="text-gray-600">·</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
              <div className="text-gray-400 font-medium mb-1">Column name examples:</div>
              {(isLC
                ? ['"LC Ref" or "Reference"', '"Maturity Date" or "Due Date"', '"Spot Rate" or "Spot"']
                : ['"Drawdown Date" or "Disbursement Date"', '"Spread" or "Margin"', '"Index" or "Benchmark"']
              ).map(ex => (
                <div key={ex} className="text-gray-600">· {ex}</div>
              ))}
            </div>
          </div>
        </div>

        {/* File picker */}
        <div
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors mb-4 ${
            fileName ? 'border-teal-600 bg-teal-900/10' : 'border-gray-700 hover:border-teal-600'
          }`}
        >
          <div className="text-2xl mb-1">📊</div>
          <div className="text-sm text-white font-medium">{fileName || 'Click to select Excel file'}</div>
          <div className="text-xs text-gray-500 mt-0.5">.xlsx or .xls · {rows.length > 0 ? `${rows.length} rows loaded` : 'No file selected'}</div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
        </div>

        {/* Preview */}
        {rows.length > 0 && previewCols.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-white">{rows.length} rows detected</span>
              <span className="text-xs text-gray-500">Preview — first 3 rows</span>
            </div>
            <div className="bg-gray-800 rounded-xl p-3 overflow-x-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    {previewCols.map(k => <th key={k} className="text-left pb-1.5 pr-4 font-medium">{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 3).map((r, i) => (
                    <tr key={i} className="border-b border-gray-700/40">
                      {previewCols.map(k => (
                        <td key={k} className="py-1.5 pr-4 text-gray-300">{String(r[k] ?? '—').slice(0, 18)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="bg-amber-900/30 border border-amber-800 rounded-xl p-3 mb-3">
            <div className="text-amber-400 text-xs font-medium mb-1">⚠ Date warnings — these fields will be saved as blank:</div>
            {warnings.map((w, i) => <p key={i} className="text-amber-300 text-xs">{w}</p>)}
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-900/30 border border-red-800 rounded-xl p-3 mb-3">
            <div className="text-red-400 text-xs font-medium mb-1">Please fix these before uploading:</div>
            {errors.map((e, i) => <p key={i} className="text-red-300 text-xs">{e}</p>)}
          </div>
        )}

        {/* Success */}
        {saved && (
          <div className="bg-teal-900/30 border border-teal-800 rounded-xl p-3 mb-3 text-teal-400 text-sm text-center">
            ✓ {rows.length} records uploaded successfully
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-700 text-gray-400 rounded-lg hover:text-gray-200">
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={rows.length === 0 || saving || saved}
            className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-500 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {saving ? 'Uploading...' : `Upload ${rows.length} Records`}
          </button>
        </div>
      </div>
    </div>
  );
}
